'use client';
import { ChangeEvent } from 'react';
import { alpha, Box, Button, TextField, Typography, useTheme } from '@mui/material';

import { useT } from '@/hooks';

interface TradeAmountInputProps {
  value: string;
  onChange: (value: string) => void;
  onQuickAdd: (amount: number) => void;
  balance?: number;
}

const QUICK_ADD_AMOUNTS = [1, 5, 10, 20];

interface QuickAddButtonProps {
  amount: number;
  onClick: (amount: number) => void;
  isDark: boolean;
  theme: any;
}

function QuickAddButton({ amount, onClick, isDark, theme }: QuickAddButtonProps) {
  const handleClick = () => onClick(amount);

  return (
    <Button
      onClick={handleClick}
      sx={{
        borderRadius: '100px',
        border: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
        fontWeight: 700,
        fontSize: { xs: '0.75rem', sm: '1rem' },
        px: { xs: 1.5, sm: 3.5 },
        py: { xs: 0.5, sm: 1 },
        textTransform: 'none',
        minWidth: 'auto',
        width: { xs: '100%', sm: 'auto' },
        flex: { xs: 1, sm: 'none' },
        bgcolor: isDark
          ? alpha(theme.palette.text.primary, 0.03)
          : alpha(theme.palette.text.primary, 0.02),
        '&:hover': {
          bgcolor: isDark
            ? alpha(theme.palette.text.primary, 0.08)
            : alpha(theme.palette.text.primary, 0.05),
          borderColor: theme.palette.text.secondary,
        },
        transition: 'all 0.2s ease',
      }}
    >
      + ${amount}
    </Button>
  );
}

export function TradeAmountInput({ value, onChange, onQuickAdd, balance }: TradeAmountInputProps) {
  const { t } = useT();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const containerBg = isDark
    ? alpha(theme.palette.text.primary, 0.08)
    : alpha(theme.palette.text.primary, 0.04);

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    // Allow numbers and a single decimal point, prevent multiple dots
    if (/^\d*\.?\d*$/.test(val)) {
      onChange(val);
    }
  }

  return (
    <Box sx={{ p: { xs: '12px', sm: '16px' }, pb: 0 }}>
      {/* Balance label */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5, px: 1 }}>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'text.secondary',
          }}
        >
          {t('trade.totalBalance', { balance: balance?.toFixed(2) || '0.00' })}
        </Typography>
      </Box>

      {/* Centered Amount Display Box */}
      <Box
        sx={{
          bgcolor: containerBg,
          borderRadius: { xs: '20px', sm: '32px' },
          p: { xs: '24px 16px', sm: '24px 8px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
          mb: 2,
          transition: 'all 0.2s ease',
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            color: 'text.primary',
            fontSize: { xs: '1.5rem', sm: '2rem' },
            mr: 1,
            lineHeight: 1,
          }}
        >
          $
        </Typography>
        <TextField
          value={value}
          onChange={handleInputChange}
          variant="standard"
          autoFocus
          placeholder="0"
          InputProps={{
            disableUnderline: true,
            sx: {
              '& input': {
                fontSize: { xs: '1.5rem', sm: '2rem' },
                fontWeight: 900,
                color: theme.palette.text.primary,
                width: 'auto',
                minWidth: '50px',
                p: 0,
                textAlign: 'left',
                lineHeight: 1,
                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                  '-webkit-appearance': 'none',
                  margin: 0,
                },
                '-moz-appearance': 'textfield',
              },
            },
          }}
        />
      </Box>

      {/* Quick Add Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, sm: 1.5 },
          justifyContent: 'center',
          flexWrap: 'wrap',
          mb: { xs: 1, sm: -2 },
        }}
      >
        {QUICK_ADD_AMOUNTS.map((amt) => (
          <QuickAddButton
            key={amt}
            amount={amt}
            onClick={onQuickAdd}
            isDark={isDark}
            theme={theme}
          />
        ))}
      </Box>
    </Box>
  );
}
